/**
 * Browser-side session-id management for the chat widget.
 *
 * The session id links anonymous turns to the same lead across page reloads.
 * It's persisted in localStorage with a last-activity timestamp and a sliding
 * retention window: a conversation is kept for at least SESSION_RETENTION_MS
 * after the last activity, after which a fresh session (and transcript) starts.
 * If storage is unavailable (private mode, sandboxed iframe) we fall back to a
 * per-tab id and accept that a refresh starts a new conversation.
 */

const SESSION_KEY_PREFIX = "nexor.chat.session_id";

/** Keep a conversation (session + transcript) this long after the last
 *  activity, so a returning visitor can resume it. Reused by the transcript
 *  cache in `./index`. The host (e.g. the landing widget) decides when to
 *  auto-resume vs. prompt "continue or start new". */
export const SESSION_RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Return the persisted session id, generating one on first use or once the
 * retention window has lapsed. Reading it slides the window forward.
 */
export function ensureSessionId(scope = "default"): string {
  const now = Date.now();
  try {
    const rec = readRecord(scope);
    if (rec && now - rec.ts < SESSION_RETENTION_MS) {
      writeRecord(scope, rec.id, now); // slide the window on activity
      return rec.id;
    }
  } catch {
    /* localStorage may be unavailable (private mode, sandboxed iframe). */
  }
  const id = `sess_${randomHex(16)}`;
  try {
    writeRecord(scope, id, now);
  } catch {
    /* ignore — id is still valid for this page load. */
  }
  return id;
}

/**
 * Mint a brand-new session id (and persist it), discarding the current one.
 * Used by the widget's "start over" action so a returning visitor can begin a
 * fresh conversation instead of resuming the persisted transcript.
 */
export function resetSession(scope = "default"): string {
  const id = `sess_${randomHex(16)}`;
  try {
    writeRecord(scope, id, Date.now());
  } catch {
    /* ignore — id is still valid for this page load. */
  }
  return id;
}

/** Bump the session's last-activity timestamp (slides the retention window). */
export function touchSession(scope = "default"): void {
  try {
    const rec = readRecord(scope);
    if (rec) writeRecord(scope, rec.id, Date.now());
  } catch {
    /* ignore */
  }
}

function storageKey(scope: string): string {
  return `${SESSION_KEY_PREFIX}.${scope}`;
}

function readRecord(scope: string): { id: string; ts: number } | null {
  const raw = window.localStorage.getItem(storageKey(scope));
  if (!raw) return null;
  try {
    const o = JSON.parse(raw);
    if (o && typeof o.id === "string" && typeof o.ts === "number") return o;
  } catch {
    /* legacy value was a plain "sess_…" string — adopt it below. */
  }
  if (raw.startsWith("sess_")) return { id: raw, ts: Date.now() };
  return null;
}

function writeRecord(scope: string, id: string, ts: number): void {
  window.localStorage.setItem(storageKey(scope), JSON.stringify({ id, ts }));
}

/**
 * Hex string from cryptographically-strong randomness when available, with
 * a Math.random() fallback for very old browsers.
 */
function randomHex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < bytes; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}
